import {
  type OrganizationType,
  type PersonSportOrganizationResponse,
  type PlayerOrgRatingAllResponse,
  type PlayerOrgRatingAllResponsePagedData,
  type ProfileOrgResponse,
  RankingType,
} from '@on3/api';
import { formatOrganizationType } from '@on3/ui-lib/utils/organization';
import clsx from 'clsx';
import { UndecidedIcon } from 'components/Icons/UndecidedIcon';
import { WithdrawnIcon } from 'components/Icons/WithdrawnIcon';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { Link } from 'components/Link';
import { ArrowRight } from 'components/Svg/ArrowRight';
import { StarRating } from 'components/UI/StarRating/StarRating';
import useAthlete from 'contexts/AthleteProvider';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';

import styles from './CollegeTransfer.module.scss';

interface ICollegeTransferProps {
  playerOrganizations: PersonSportOrganizationResponse[];
  collegeData?: ProfileOrgResponse | null;
  rankingsAll?: PlayerOrgRatingAllResponsePagedData | null;
}

export const CollegeTransfer = ({
  collegeData,
  playerOrganizations,
  rankingsAll,
}: ICollegeTransferProps) => {
  const { organization, expMin, expMax, age } = collegeData || {};
  const { athlete } = useAthlete();
  const currentOrg =
    (playerOrganizations?.length && playerOrganizations[0]) || {};
  const { status: commitStatus, rpm } = currentOrg || {};

  const undecided = commitStatus?.type === 'None';
  const hasWithdrawn = commitStatus?.transferWithdrawn;
  const hasPredictions = (rpm ?? [])?.length > 0;

  // Rankings
  const hasRankings = !!rankingsAll?.list?.length;
  const latestRankingType = rankingsAll?.list?.[0]?.ratings?.[0]?.rankingType;

  const [rankingIndex, setRankingIndex] = useState(0);
  const [rankingLabel, setRankingTypeLabel] = useState(
    latestRankingType === RankingType.TransferPortal
      ? 'Transfer Portal'
      : formatOrganizationType(
          (rankingsAll?.list?.[0]?.organization?.orgType as OrganizationType) ??
            '',
        ),
  );
  const ratingType = athlete.primaryPosition?.sport?.isIndustryRankable
    ? 'Industry'
    : 'On3';

  const activeRanking = useMemo(
    () =>
      rankingsAll?.list?.[rankingIndex]?.ratings?.find(
        (rating) => rating?.type === ratingType,
      ),
    [rankingsAll?.list, rankingIndex, ratingType],
  );

  const handleRankingSelect = (
    ranking: PlayerOrgRatingAllResponse,
    index: number,
  ) => {
    setRankingIndex(index);
    setRankingTypeLabel(
      ranking?.ratings?.[0]?.rankingType === RankingType.TransferPortal
        ? 'Transfer Portal'
        : formatOrganizationType(
            (ranking?.organization?.orgType as OrganizationType) ?? '',
          ),
    );
  };

  const isTransferPortalRanking =
    activeRanking?.rankingType === RankingType.TransferPortal;

  const linkSport = activeRanking?.sport?.toLowerCase() || 'football';
  const linkYear = activeRanking?.rankingYear || new Date().getFullYear();
  const linkPosition = activeRanking?.positionAbbr?.toLowerCase() || 'qb';
  const linkState = activeRanking?.stateAbbr?.toLowerCase() || 'al';
  const baseLink = isTransferPortalRanking
    ? `/transfer-portal/industry/${linkSport}/${linkYear}/`
    : `/rivals/rankings/player/${linkSport}/${linkYear}/`;
  const positionLink = `${baseLink}?position=${linkPosition}`;
  const stateLink = `${baseLink}?state=${linkState}`;

  return (
    <section className={styles.block}>
      <h2>Transfer Portal</h2>
      <div className={styles.teams}>
        <div className={styles.entered}>
          <div className={styles.team}>
            <On3Avatar
              className={styles.logo}
              domain={organization?.asset?.domain}
              height={100}
              source={organization?.asset?.source}
              teamAvatar
              type={organization?.asset?.type}
              width={100}
            />
          </div>

          {commitStatus?.transferEntered && (
            <div className={styles.label}>
              <span>
                {`Entered - `}
                {format(new Date(commitStatus.transferEntered), 'M/d/yyyy')}
              </span>
            </div>
          )}
        </div>

        <ArrowRight className={styles.arrow} />

        <div className={styles.statuses}>
          {!undecided && !hasWithdrawn && (
            <div className={clsx(styles.status, styles.committed)}>
              <div className={styles.team}>
                <TeamAvatar
                  className={styles.logo}
                  height={30}
                  slug={commitStatus?.committedAsset?.slug}
                  src={commitStatus?.committedAsset?.assetUrl}
                  width={30}
                />
              </div>

              {commitStatus?.date && (
                <div
                  className={styles.label}
                  style={{
                    backgroundColor:
                      commitStatus?.committedAsset?.primaryColor ?? '#252525',
                  }}
                >
                  {'Hard Commit - '}
                  {format(new Date(commitStatus?.date), 'M/d/yyyy')}
                </div>
              )}
            </div>
          )}

          {undecided && !hasPredictions && (
            <div className={clsx(styles.status, styles.undecided)}>
              <UndecidedIcon className={styles.icon} />
              <span>Undecided</span>
            </div>
          )}

          {undecided && hasPredictions && (
            <div className={clsx(styles.status, styles.predictions)}>
              {rpm?.slice(0, 2).map((org, index) => {
                const orgDistance = org?.distance ?? -1;
                const distanceText =
                  orgDistance > -1 ? Math.round(orgDistance) : '-';

                return (
                  <div className={styles.prediction} key={index}>
                    {org.percent && (
                      <span className={styles.percentage}>
                        {org.percent.toFixed(1)}%
                      </span>
                    )}

                    <TeamAvatar
                      className={styles.logo}
                      height={30}
                      slug={org?.organization?.slug}
                      src={org?.organization?.assetUrl}
                      width={30}
                    />

                    <div>
                      {distanceText}
                      <span aria-label="Miles">mi</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasWithdrawn && (
            <div className={clsx(styles.status, styles.withdrawn)}>
              <WithdrawnIcon className={styles.icon} />
              <span>Withdrawn</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.info}>
        {hasRankings && (
          <>
            <div className={styles.tabs}>
              {rankingsAll.list
                ?.filter(
                  (r) =>
                    !!r.organization &&
                    !!r.ratings?.some((rat) => rat.type === 'Industry'),
                ) // Filter out rankings without an organization or industry rating
                .map((ranking, i) => {
                  const rankingType = ranking?.ratings?.[0]?.rankingType;
                  const rankingTypeLabel =
                    rankingType === RankingType.TransferPortal ? (
                      <>
                        <span>Transfer Portal</span>
                        <span>{`(${ranking.organization?.abbreviation})`}</span>
                      </>
                    ) : (
                      formatOrganizationType(
                        (ranking.organization?.orgType as OrganizationType) ??
                          '',
                      )
                    );

                  return (
                    <button
                      className={clsx(styles.button, {
                        [styles.selected]: i === rankingIndex,
                      })}
                      key={i}
                      onClick={() => handleRankingSelect(ranking, i)}
                    >
                      {rankingTypeLabel}
                    </button>
                  );
                })}
            </div>
            {activeRanking && (
              <>
                <div className={styles.row}>
                  <span className={styles.label}>{rankingLabel} Rating</span>
                  <Link
                    className={styles.ratings}
                    fallback={'div'}
                    href={baseLink}
                  >
                    <StarRating stars={activeRanking.stars} />
                    <span className={styles.rating}>
                      {activeRanking.rating?.toFixed(2)}
                    </span>
                  </Link>
                </div>
                <div className={styles.row}>
                  <span className={styles.label}>{rankingLabel} Rankings</span>
                  <div className={styles.rankings}>
                    <div className={styles.ranking}>
                      <span>Natl</span>
                      <Link href={baseLink}>
                        {activeRanking?.overallRank || '-'}
                      </Link>
                    </div>
                    <div className={styles.ranking}>
                      <span>
                        {isTransferPortalRanking
                          ? activeRanking.positionAbbr
                          : 'Pos'}
                      </span>
                      <Link href={positionLink}>
                        {activeRanking?.positionRank || '-'}
                      </Link>
                    </div>
                    {!isTransferPortalRanking && (
                      <div className={styles.ranking}>
                        <span>St</span>
                        <Link href={stateLink}>
                          {activeRanking?.stateRank || '-'}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
        <div className={styles.row}>
          <span className={styles.label}>Experience</span>
          <span>
            {expMin} -{' '}
            {expMax && expMax >= new Date().getFullYear() ? 'present' : expMax}
          </span>
        </div>
        {!!age && (
          <div className={styles.row}>
            <span className={styles.label}>Age</span>
            <span>{age}</span>
          </div>
        )}
      </div>
    </section>
  );
};
